"use client"

import { useEffect, useState } from "react"
import Image from "next/image"
import { useTranslations } from "@/lib/intl/use-translations"
import { Button, ArrowRightIcon } from "@/components/ui/button"
import { BookingCta, PRODUCT_IDS } from "@/components/ui/booking-cta"
import {
  ProductInfoButton,
  type BiomarkerGroup,
} from "@/components/ui/product-info-modal"
import { ADDON_INFO } from "@/data/product-info"
import {
  AddTestsIcon,
  ImpactAreaIcon,
  CheckCircleIcon,
} from "@/components/ui/icons"
import { ReviewedByPhysiciansIcon } from "@/components/ui/reviewed-by-physicians-icon"
import { buildFallback, fetchPrices, type PriceData } from "@/lib/pricing"
import { formatNumber } from "@/lib/format"
import { isWaitlistOnly, getCountryConfigStatic } from "@/i18n/config"
import { goKeys } from "@/i18n/go-keys"
import { useCountry } from "@/components/layout/country-provider"
import { blobMediaUrl } from "@/lib/blob-url"

interface BloodTestCardProps {
  /**
   * Analytics label for the CTA, e.g. "pricing-blood" or "how-it-works-blood".
   * When omitted no tracking event is emitted, but the CTA still routes through
   * `BookingCta` so NL waitlist behavior is preserved.
   */
  trackingLocation?: string
  /**
   * Override live Stripe prices. When omitted the component fetches the current
   * prices from `/api/prices` on mount. If the request fails or returns no
   * `blood` price, the card falls back to the static `299` label.
   */
  prices?: PriceData | null
  /** When false, do not trigger a client fetch if `prices` is still null */
  fetchIfMissing?: boolean
  /**
   * Render a `(i)` info button next to the title that opens the product info
   * modal. Defaults to `false` — test detail pages don't need it because the
   * whole page IS the detail view.
   */
  showInfoButton?: boolean
  /** Pre-grouped biomarkers from CMS for the blood test info modal */
  bloodBiomarkerGroups?: BiomarkerGroup[]
  /**
   * Override the first trust bullet, which defaults to
   * `pricing.trustReviewedByPhysicians` ("Reviewed by Swiss physicians").
   * Set by surfaces that must not make a Swiss-clinician claim — the German
   * Munich LP passes a nationality-free label. Leave unset everywhere else so
   * the shared string stays the single source of truth.
   */
  reviewedByLabel?: string
  /**
   * Override the third trust bullet, which defaults to
   * `pricing.trustAddTestsAnytime` — a string that offers MRI imaging
   * ("Erweiterbar mit weiteren Tests oder MRT"). Set by surfaces that don't
   * sell the scan, e.g. the German blood-only /pricing page, which passes the
   * MRI-free `pricing.trustAddTestsAnytimeMri` instead. Leave unset everywhere
   * else so the shared string stays the single source of truth.
   */
  addTestsLabel?: string
  /**
   * Override the blurb under the title, which defaults to
   * `pricing.bloodTestDesc`, and the first "includes" bullet, which defaults to
   * `pricing.bloodFeature1`. Both name a biomarker count, and that count is a
   * property of the panel a given surface sells — the German ad LP passes
   * `go.bloodtestCardDesc` / `go.bloodtestCardFeature1` because its panel is 67
   * markers where the shared strings still say 80+. Leave unset everywhere else
   * so the shared strings stay the single source of truth.
   */
  descriptionLabel?: string
  bloodFeatureLabel?: string
}

export function BloodTestCard({
  trackingLocation,
  prices: externalPrices,
  fetchIfMissing = true,
  showInfoButton = false,
  bloodBiomarkerGroups,
  reviewedByLabel,
  addTestsLabel,
  descriptionLabel,
  bloodFeatureLabel,
}: BloodTestCardProps) {
  const t = useTranslations("pricing")
  const ut = useTranslations("ui")
  const gt = useTranslations("go")
  const { code: country, currency } = useCountry()
  // Waitlist market: BookingCta already opens the waitlist modal, but the
  // label must say so too — "Book…" over a waitlist action misleads.
  const waitlist = isWaitlistOnly(country)
  // DE renders this card as its entire blood offer, so the three deliberate DE
  // decisions that lived in the narrow comparison card (AHD-6680) have to hold
  // here too, or porting the format would quietly change what Germany is told:
  // GOÄ billing makes the figure a "from" price, the CTA lands on the generic
  // booking entry rather than deep-linking the blood product, and it reads
  // "Check-up buchen". None of the three is an oversight — don't drop them.
  const isDe = country === "de"
  // Seed with country-aware fallback so SSR / first paint already show
  // the correct currency and amount on EUR markets (e.g. 279 EUR on /de/de,
  // not 299 CHF).
  const [liveP, setLiveP] = useState<PriceData | null>(
    externalPrices ?? buildFallback(country)
  )

  useEffect(() => {
    if (externalPrices) {
      setLiveP(externalPrices)
      return
    }
    if (fetchIfMissing) {
      fetchPrices(country).then(setLiveP)
    }
  }, [externalPrices, country, fetchIfMissing])

  // "299" is the hardcoded CHF figure for the advanced blood test. We only
  // fall back to it on CHF markets — on EUR markets an absent live price
  // means "no authoritative EUR figure yet" and the price line is hidden
  // rather than mislabeling CHF with a € symbol.
  const livePrice = liveP?.prices.blood
  // `prices` are raw numeric strings; format for display so the amount carries
  // the locale separator (kept raw in the map for JSON-LD).
  const bloodPrice = livePrice
    ? formatNumber(Number(livePrice), country)
    : currency === "CHF"
      ? "299"
      : ""
  const displayCurrency = liveP?.currency ?? currency
  // German convention (DIN 5008) puts the symbol after the amount, where CHF
  // markets correctly put the code before it. Derived from Intl rather than
  // written as a literal, so no currency string lives in the component.
  // DE-scoped on purpose: nl-NL and de-AT put the symbol *before* the amount,
  // so the general per-locale fix is its own change — AHD-12918.
  const deCurrencySymbol =
    isDe && bloodPrice
      ? new Intl.NumberFormat(getCountryConfigStatic(country)?.intlLocale, {
          style: "currency",
          currency: displayCurrency,
        })
          .formatToParts(0)
          .find((part) => part.type === "currency")?.value
      : undefined

  return (
    <div>
      <div className="flex flex-col overflow-hidden rounded-[16px] bg-white md:flex-row">
        {/* Left side */}
        <div className="flex flex-col gap-6 p-8 md:w-1/2">
          <div className="flex flex-col gap-4">
            <div className="size-[64px] shrink-0 overflow-hidden rounded-full">
              <Image
                src={blobMediaUrl(
                  "/images/addons/advanced-blood-test-card.png"
                )}
                alt={t("advancedBloodTestTitle")}
                width={64}
                height={64}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1">
                <h3 className="type-headline-md text-neutral-900">
                  {t("advancedBloodTestTitle")}
                </h3>
                {showInfoButton && ADDON_INFO.advancedBloodTest && (
                  <ProductInfoButton
                    data={ADDON_INFO.advancedBloodTest}
                    biomarkerGroups={bloodBiomarkerGroups}
                  />
                )}
              </div>
              <p className="type-body-md text-neutral-500">
                {t("bloodDuration")}
              </p>
              <p className="type-body-lg text-neutral-900">
                {descriptionLabel ?? t("bloodTestDesc")}
              </p>
            </div>
          </div>

          <div className="flex items-baseline gap-1">
            {bloodPrice ? (
              <>
                {/* DE: GOÄ billing means the shown total is a "from" price.
                    Without this prefix an indicative figure reads as a firm
                    one. Same string the narrow card prints ("Ab"). */}
                {isDe && (
                  <span className="type-body-md text-neutral-500">
                    {gt(goKeys.bloodtestCompareStartingAt)}
                  </span>
                )}
                {!deCurrencySymbol && (
                  <span className="type-body-md text-neutral-500">
                    {displayCurrency}
                  </span>
                )}
                <span className="type-display-sm text-neutral-900">
                  {bloodPrice}
                </span>
                {deCurrencySymbol && (
                  <span className="type-body-md text-neutral-500">
                    {deCurrencySymbol}
                  </span>
                )}
              </>
            ) : (
              // Pre-launch EUR markets (DE/NL) don't yet have authoritative
              // pricing. Show a holding dash rather than fabricated EUR.
              // [TODO: confirm DE pricing]
              <span className="type-display-sm text-neutral-900">—</span>
            )}
          </div>

          <div className="mt-auto">
            {/* DE deliberately uses the generic booking entry
                (bookingUrlForCountry → ?step=product&country=de) instead of
                deep-linking the blood product; hardcoding productId here would
                silently send German users somewhere else. */}
            <BookingCta
              {...(isDe ? {} : { productId: PRODUCT_IDS.bloodTest })}
              trackingLabel="Book Advanced blood test"
              trackingLocation={trackingLocation}
              className="w-full"
            >
              <Button
                variant="primary"
                className="group w-full"
                iconRight={
                  <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
                }
              >
                {waitlist
                  ? ut("joinWaitlist")
                  : isDe
                    ? ut("bookCheckup")
                    : t("bookAdvancedBlood")}
              </Button>
            </BookingCta>
          </div>
        </div>

        {/* Right side */}
        <div className="flex flex-col gap-6 p-8 md:w-1/2">
          {/* Trust signals — shared styling with PricingCard highlights */}
          <div className="flex items-start">
            <div className="flex flex-1 flex-col items-center gap-1 rounded-lg px-1 py-2">
              <div className="flex size-10 items-center justify-center">
                <ReviewedByPhysiciansIcon
                  size={24}
                  className="text-neutral-500"
                />
              </div>
              <p className="type-body-sm text-center font-medium text-neutral-900">
                {reviewedByLabel ?? t("trustReviewedByPhysicians")}
              </p>
            </div>
            <div className="flex flex-1 flex-col items-center gap-1 rounded-lg px-1 py-2">
              <div className="flex size-10 items-center justify-center">
                <ImpactAreaIcon size={24} className="text-neutral-500" />
              </div>
              <p className="type-body-sm text-center font-medium text-neutral-900">
                {t("trustPersonalActionPlan")}
              </p>
            </div>
            <div className="flex flex-1 flex-col items-center gap-1 rounded-lg px-1 py-2">
              <div className="flex size-10 items-center justify-center">
                <AddTestsIcon size={24} className="text-neutral-500" />
              </div>
              <p className="type-body-sm text-center font-medium text-neutral-900">
                {addTestsLabel ?? t("trustAddTestsAnytime")}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <div className="px-2">
              <span className="type-label-md text-neutral-500">
                {t("bloodIncludesLabel")}
              </span>
            </div>
            <div className="flex flex-col">
              {(
                [
                  "bloodFeature1",
                  "bloodFeature2",
                  "bloodFeature3",
                  "bloodFeature4",
                  "bloodFeature5",
                  "bloodFeature6",
                ] as const
              ).map((fKey) => (
                <div
                  key={fKey}
                  className="flex min-h-10 items-center gap-2 rounded-lg px-2 py-1.5"
                >
                  <CheckCircleIcon className="flex-shrink-0" />
                  <span className="type-body-md md:type-body-lg text-neutral-900">
                    {fKey === "bloodFeature1"
                      ? (bloodFeatureLabel ?? t(fKey))
                      : t(fKey)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
